import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error('Wypełnij wszystkie pola');
      return;
    }

    if (password.length < 6) {
      toast.error('Hasło musi mieć minimum 6 znaków');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Hasła nie są identyczne');
      return;
    }

    if (!token) {
      toast.error('Brak tokenu resetu. Poproś o nowy link.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Hasło zostało zmienione!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Wystąpił błąd. Link mógł wygasnąć.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[var(--crm-bg-main)] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 md:p-8 text-center">
          <img src="/logo.png" alt="Trendhomes" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-manrope font-bold text-white mb-2">Nieprawidłowy link</h1>
          <p className="text-[var(--crm-text-secondary)] mb-6">
            Link do resetu hasła jest nieprawidłowy lub wygasł.
          </p>
          <Link to="/forgot-password">
            <Button className="w-full bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">
              Poproś o nowy link
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--crm-bg-main)] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 md:p-8">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="Trendhomes" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-manrope font-bold text-[var(--crm-text-primary)]">
            {success ? 'Hasło zmienione!' : 'Ustaw nowe hasło'}
          </h1>
          <p className="text-[var(--crm-text-secondary)] mt-2 text-sm">
            {success 
              ? 'Możesz teraz zalogować się nowym hasłem.'
              : 'Wpisz nowe hasło dla swojego konta.'
            }
          </p>
        </div>

        {success ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white"
            >
              Przejdź do logowania
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-[var(--crm-text-secondary)]">Nowe hasło</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--crm-text-secondary)]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 6 znaków"
                  className="pl-10 pr-10 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-white"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--crm-text-secondary)] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-[var(--crm-text-secondary)]">Powtórz hasło</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--crm-text-secondary)]" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Powtórz nowe hasło"
                  className="pl-10 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-white"
                  data-testid="reset-password-confirm"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-password-submit"
              className="w-full bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white"
            >
              {loading ? 'Zapisywanie...' : 'Ustaw nowe hasło'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
};
